import { useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { format } from 'date-fns';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
// import { fetchTiposCanais } from 'store/reducers/canais';
import {
  addContatoV2,
  ContatoInterface,
  contatosSelector,
  fetchCustomFields,
} from 'store/reducers/contatos';
import * as validators from 'utils/validators';

import CustomFields from './CustomFields';
import InternacionalPhoneInput from './InternacionalPhoneInput';

type ModalAddContatoProps = {
  open: boolean;
  contato?: ContatoInterface;
  onClose: () => void;
  onSuccess?: () => void;
};

const useStyles = makeStyles(({ spacing, typography }) => ({
  textField: {
    marginBottom: spacing(2),
  },
  dialog: {
    padding: 0,
    margin: 0,
  },
  input: {
    fontSize: typography.pxToRem(14),
  },
  actions: { position: 'sticky', bottom: 0, zIndex: 10 },
}));

type FormValues = {
  nome: string;
  contato: string;
  observacao: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export default function ModalAddContato(props: ModalAddContatoProps) {
  const { open, contato, onClose, onSuccess } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  // const { tiposCanais, loadingTiposCanais } = useAppSelector(canaisSelector);
  const { customFields, loadingCustomFields } =
    useAppSelector(contatosSelector);

  const { dynamicInitialValues, dynamicSchema } =
    validators.buildDynamicFormikSchema(customFields);

  const fixedInitialValues = {
    nome: contato?.nome ?? '',
    contato: contato?.contato ?? '',
    observacao: contato?.observacao ?? '',
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      ...fixedInitialValues,
      ...dynamicInitialValues,
      // já tras o primeiro tipo de canal selecionado se tiver apenas um tipo na lista
      // tipoCanal: tiposCanais[0]?.key || '',
    },
    validationSchema: validators.adicionarContatoSchema
      .pick(Object.keys(fixedInitialValues))
      .concat(dynamicSchema),
    onSubmit: async values => {
      const fixedFields = Object.keys(fixedInitialValues);

      const customData = Object.entries(values)
        .filter(
          ([key, value]) =>
            !fixedFields.includes(key) && value !== '' && value !== null,
        )
        .map(([id, value]) => {
          if (value instanceof Date && value.toString() !== 'Invalid Date') {
            return {
              id,
              value: format(value, 'yyyy-MM-dd'),
            };
          }
          return {
            id,
            value,
          };
        });

      const payload = {
        name: formik.values.nome,
        phoneNumber: formik.values.contato,
        observation: formik.values.observacao,
        customFields: customData,
      };

      await dispatch(addContatoV2(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('O contato foi adicionado a sua lista de contatos.');

          if (onSuccess) onSuccess();

          onClose();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const handleClose = () => {
    if (!formik.isSubmitting) onClose();
  };

  useEffect(() => {
    dispatch(fetchCustomFields());
  }, [dispatch]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open && !loadingCustomFields}
      onClose={handleClose}
      PaperProps={{ className: classes.dialog }}
      TransitionProps={{
        // onEnter: () => dispatch(fetchTiposCanais()),
        onExited: () => formik.resetForm(),
      }}
    >
      <DialogHeader title="Adicionar contato" onClose={handleClose} />

      <Box pl={2} pr={2}>
        <Alert severity="info">
          <Typography variant="caption">
            Este recurso está disponível apenas para os contatos do WhatsApp.
          </Typography>
        </Alert>
      </Box>

      <FormikProvider value={formik}>
        <DialogForm
          onSubmit={formik.handleSubmit}
          loading={formik.isSubmitting}
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs>
                <TextField
                  autoFocus
                  fullWidth
                  id="nome"
                  name="nome"
                  label="Nome"
                  type="text"
                  variant="outlined"
                  placeholder="Nome"
                  InputLabelProps={{
                    className: classes.input,
                  }}
                  inputProps={{
                    className: classes.input,
                  }}
                  className={classes.textField}
                  value={formik.values.nome}
                  onChange={formik.handleChange}
                  error={formik.touched.nome && Boolean(formik.errors.nome)}
                  helperText={formik.touched.nome && formik.errors.nome}
                />
              </Grid>

              <Grid item xs>
                <InternacionalPhoneInput
                  value={formik.values.contato}
                  onChange={(contato: string) =>
                    formik.setFieldValue('contato', contato)
                  }
                  error={
                    formik.touched.contato && Boolean(formik.errors.contato)
                  }
                  helperText={formik.touched.contato && formik.errors.contato}
                />
              </Grid>
            </Grid>

            {/* OBS.: Tem a possibilidade de voltar este recurso */}
            {/* <TextField
            select
            fullWidth
            id="tipoCanal"
            name="tipoCanal"
            label="Tipo do Canal"
            variant="outlined"
            className={classes.textField}
            value={formik.values.tipoCanal}
            onChange={formik.handleChange}
            error={formik.touched.tipoCanal && Boolean(formik.errors.tipoCanal)}
            helperText={formik.touched.tipoCanal && formik.errors.tipoCanal}
            disabled={loadingTiposCanais}
            InputProps={{
              startAdornment: loadingTiposCanais && (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ),
            }}
          >
            {tiposCanais.map(tipoCanal => (
              <MenuItem key={tipoCanal.key} value={tipoCanal.key}>
                {tipoCanal.value}
              </MenuItem>
            ))}
          </TextField> */}

            <TextField
              id="observacao"
              type="text"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label="Observação (opcional)"
              variant="outlined"
              placeholder="Digite aqui sua observação..."
              value={formik.values.observacao}
              onChange={formik.handleChange}
              error={
                formik.touched.observacao && Boolean(formik.errors.observacao)
              }
              helperText={formik.touched.observacao && formik.errors.observacao}
            />

            {!loadingCustomFields && customFields?.length > 0 && (
              <>
                <Typography variant="body2" style={{ marginBlock: 16 }}>
                  Campos personalizados
                </Typography>

                <CustomFields customFields={customFields} />
              </>
            )}
          </DialogContent>

          <DialogActions className={classes.actions}>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              disabled={formik.isSubmitting}
              variant="contained"
              color="primary"
              type="submit"
            >
              Pronto
            </Button>
          </DialogActions>
        </DialogForm>
      </FormikProvider>
    </Dialog>
  );
}
